import { ErrorResponse } from '@models/error.response';
import { TranslateService } from '@ngx-translate/core';
import { MainComponent } from '../main/main.component';
import { of, from } from 'rxjs';
import { ActionType } from '@type/action.type';
import { AppConstant } from '@constants/app.constant';
import { NgForm } from '@angular/forms';
import { FormValidationUtil } from '@utils/form.validation';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import swal from 'sweetalert2';
import { switchMap, map, flatMap, filter, toArray, mergeMap, reduce } from 'rxjs/operators';
import { User } from '@app/models/user';
import { Group } from '@app/models/group';
import { UserService } from '@app/services/user.service';
import { GroupService } from '@app/services/group.service';
import { AdminUserService } from '@app/services/admin-user-service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NgSelectUtil } from '@app/utils/ng-select-utils';
import { ModulePathConstant } from '@app/constants/modulePath.constant';

@Component({
    templateUrl: './group-setup.component.html',
    styleUrls: ['./group-setup.component.scss']
})
export class GroupSetupComponent {
    @ViewChildren(NgSelectComponent) ngSelects:QueryList<NgSelectComponent>;
    selectAll = NgSelectUtil.selectAll
    @ViewChild('groupForm', { static: false }) groupForm: NgForm;
    isFieldError = FormValidationUtil.isFieldError;
    action: ActionType;
    loading: boolean = false;
    userOptions: User[] = [];
    group: Group;

    constructor(
        private adminUserSerice: AdminUserService,
        private groupService: GroupService,
        private router: Router,
        private route: ActivatedRoute,
        private mainComponent: MainComponent,
        private translate: TranslateService,
        public appConstant: AppConstant) {
    }

    ngOnInit(): void {
        this.group = new Group();

        this.route.params
            .pipe(
                switchMap((params: Params) => {
                    this.loading = true;
                    if (params['id'] === 'new') {
                        this.action = 'CREATE';
                    } else {
                        this.action = 'EDIT';
                    }
                    return of(params);
                }),
                switchMap((params: Params) => {
                    return this.adminUserSerice.findAll()
                        .pipe(
                            map(users => {
                                this.userOptions = users;
                                return params;
                            })
                        );
                }),
                switchMap((params: Params) => {
                    if (this.action == 'CREATE') {
                        return of(new Group());
                    } else {
                        return this.groupService.get(params['id'])
                    }
                })
            )
            .subscribe((group: Group) => {
                this.group = group;
                this.loading = false;
            });

    }


    save(): void {

        //check form for just clicking save without any input
        for (let key of Object.keys(this.groupForm.controls)) {
            this.groupForm.controls[key].markAsDirty();
        }

        if (this.groupForm.valid) {

            switch (this.action) {
                case 'CREATE':
                    this.create();
                    break
                case 'EDIT':
                    this.update();
                    break;
            }
        }
    }

    private create(): void {

        this.mainComponent.enableLoadingMask();
        this.loading = true;

        this.groupService.insert(this.group)
            .subscribe(() => {
                this.mainComponent.disableLoadingMask();
                this.loading = false;

                this.router.navigate([ModulePathConstant.GROUP_PATH]);

                this.translate.get(ModulePathConstant.GROUP_MODULE)
                    .pipe(
                        switchMap(typeStr => this.translate.get(AppConstant.HAS_BEEN_ADDED, { type: typeStr }))
                    )
                    .subscribe(res => {
                        swal.fire({
                            type: 'success',
                            title: res
                        });
                    });
            },
                (error: ErrorResponse) => {
                    this.mainComponent.disableLoadingMask();
                    this.loading = false;
                    from(error.errors)
                        .pipe(
                            mergeMap(error => this.translate.get(error.errorCode)),
                            reduce((acc, value) => acc + value + '<br/>', '')
                        )
                        .subscribe(msg => {

                            this.translate.get(ModulePathConstant.GROUP_MODULE)
                                .pipe(
                                    switchMap(typeStr => this.translate.get(AppConstant.FAIL_TO_ADD, { type: typeStr }))
                                )
                                .subscribe(title => {
                                    swal.fire({
                                        type: 'error',
                                        title: title,
                                        html: msg
                                    });
                                });

                        });
                });
    }

    private update(): void {

        this.mainComponent.enableLoadingMask();
        this.loading = true;

        this.groupService.update(this.group)
            .subscribe(group => {
                this.group = group;
                this.mainComponent.disableLoadingMask();
                this.loading = false;

                this.router.navigate([ModulePathConstant.GROUP_PATH]);

                this.translate.get(ModulePathConstant.GROUP_MODULE)
                    .pipe(
                        switchMap(typeStr => this.translate.get(AppConstant.HAS_BEEN_UPDATED, { type: typeStr }))
                    )
                    .subscribe(res => {
                        swal.fire({
                            type: 'success',
                            title: res
                        });
                    });

            },
                (error: ErrorResponse) => {
                    this.mainComponent.disableLoadingMask();
                    this.loading = false;


                    from(error.errors)
                        .pipe(
                            mergeMap(error => this.translate.get(error.errorCode)),
                            reduce((acc, value) => acc + value + '<br/>', '')
                        )
                        .subscribe(msg => {

                            this.translate.get(ModulePathConstant.GROUP_MODULE)
                                .pipe(
                                    switchMap(typeStr => this.translate.get(AppConstant.FAIL_TO_UPDATE, { type: typeStr }))
                                )
                                .subscribe(title => {
                                    swal.fire({
                                        type: 'error',
                                        title: title,
                                        html: msg
                                    });
                                });
                        });
                })

    }

    cancel(): void {
        this.router.navigate([ModulePathConstant.GROUP_PATH]);
    }

}
